import * as React from 'react';

import { Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, Radio, RadioGroup } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import { type DialogProps } from '@mui/material/Dialog/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { StyledLoadingButton } from '../../forms/LoadingButton';

const RadioDivider = styled.div`
    border-bottom: 1px solid ${(props) => props.theme.colors.border};
`;

const StyledFormGroup = styled(FormGroup)`
    margin-left: 2.25rem;
`;

export enum LaunchType {
    Everyone = 'everyone',
    Specific = 'specific'
}

export type FormData = {
    type: LaunchType,
    breakoutRooms: { [index: string]: boolean }
};

export type LaunchToBreakoutRoomsDialogProps = DialogProps & {
    title: string;
    breakoutRooms: string[];
    launching?: boolean;
    onClose: () => void;
    onLaunch: (data: { type: LaunchType, breakoutRooms: string[] }) => void;
};

/**
 * Dialog to choose breakout rooms for launching resources.
 * @param props
 * @constructor
 */
export const LaunchToBreakoutRoomsDialog = (props: LaunchToBreakoutRoomsDialogProps) => {
    const { title, open, launching, onClose, onLaunch, breakoutRooms, ...rest } = props;
    const { t: tCommon } = useTranslation('common');
    const { t: tBreakoutRooms } = useTranslation('breakout_room');

    const methods = useForm<FormData>({
        defaultValues: {
            type: LaunchType.Everyone
        }
    });
    const { control, watch, handleSubmit } = methods;

    const launchType = watch('type');

    // Disable launch button until user chooses at least one breakout room.
    const launchDisabled = launchType === LaunchType.Specific &&
        !Object.values(watch('breakoutRooms')).some((val) => val === true);

    const disableCheckboxes = watch('type') === LaunchType.Everyone;

    const handleLaunch = (data: FormData) => {
        if (data.type === LaunchType.Everyone) {
            onLaunch({ ...data, breakoutRooms: [] });
            return;
        }

        const selectedBreakoutRooms = Object.keys(data.breakoutRooms).filter((key) => data.breakoutRooms[key]);

        const mainSessionIndex = selectedBreakoutRooms.indexOf('main');
        if (mainSessionIndex !== -1) {
            selectedBreakoutRooms[mainSessionIndex] = ''; // Empty breakout room value means main classroom
        }

        onLaunch({ ...data, breakoutRooms: selectedBreakoutRooms });
    };

    const renderBreakoutCheckboxes = () => {
        const transformed = breakoutRooms.filter((i) => i !== ''); // remove empty entries
        transformed.unshift('main');

        return transformed.map((breakoutRoom) => (
            <Controller key={breakoutRoom} name={`breakoutRooms.${breakoutRoom}`} render={({ field }) => (
                <FormControlLabel control={<Checkbox {...field} value={breakoutRoom} disabled={disableCheckboxes} />} label={breakoutRoom === 'main' ? tCommon('main-classroom') : breakoutRoom} />
            )}/>
        ));
    };

    return (
        <Dialog
            {...rest}
            open={open}
            onClose={onClose}
            keepMounted
            aria-describedby="launch-to-label"
            aria-labelledby="launch-to-dialog-title"
            maxWidth="sm"
            fullWidth
            data-testid="launch-to-breakout-rooms-dialog"
        >
            <DialogTitle id="launch-to-dialog-title">{title}</DialogTitle>
            <DialogContent>
                <FormProvider {...methods}>
                    <form>
                        <FormControl sx={{ width: '100%' }}>
                            <FormLabel id="launch-to-label">{tBreakoutRooms('launchTo')}</FormLabel>
                            <Controller
                                control={control}
                                name="type"
                                render={({ field }) => (
                                    <RadioGroup
                                        aria-labelledby="launch-to-label"
                                        {...field}
                                    >
                                        <FormControlLabel value={LaunchType.Everyone} control={<Radio />} label={tBreakoutRooms('everyone') as string} />
                                        <RadioDivider />
                                        <FormControlLabel value={LaunchType.Specific} control={<Radio />} label={tBreakoutRooms('specificBreakoutRooms') as string} />
                                    </RadioGroup>
                                )}
                            />
                        </FormControl>
                        <StyledFormGroup>
                            {renderBreakoutCheckboxes()}
                        </StyledFormGroup>
                    </form>
                </FormProvider>
            </DialogContent>
            <DialogActions>
                <Button disabled={launching} variant="outlined" onClick={onClose}>{tCommon('cancel')}</Button>
                <StyledLoadingButton
                    disabled={launching || launchDisabled}
                    loading={launching}
                    variant={launching ? 'outlined' : 'contained'}
                    color="primary"
                    onClick={handleSubmit(handleLaunch)}
                    aria-busy={launching}
                    data-testid="launch-button"
                >{tBreakoutRooms('launch')}</StyledLoadingButton>
            </DialogActions>
        </Dialog>
    );
};
